import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import GenericCollapsibleList from "../GenericCollapsibleList";
import { Search } from "styled-icons/material/Search";
import { CalendarModalIcon, CheckCollapsibleIcon } from "src/components/Icons";
import { ArrowDownIcon } from "src/components/Avata/styles";
import * as Ui from "./styles";
import DateFilter from "../../components/DateFilter";
import ToolTip from "../../components/ToolTip";
import { fetchSurveysWithCrossedQuestions } from "src/stores/actions/evolutiveCross";
import { useDispatch, useSelector } from "react-redux";
import CrossQuestionsAlternatives from "../../widget/CrossQuestionsAlternatives";
import { dateFormat } from "src/util/formatters/dateFormat";
import { Loading } from "src/components";
import useFlashMessage from "src/hooks/FlashMessage";
import { StepManager } from "../../managers";
import { EVOLUTIVE_CROSSING_NETWORK_ERROR } from "src/sections/GroupCards/EvolutiveCrossChartCard/config/util/Constantes";
import ArrowRightIcon from 'src/components/Icons/arrowRight';


export default function SurveysCollapsibleList({ customerId, dashboardSurveyId }) {
  // states
  const [selectedItems, setSelectedItems] = useState([]);
  const [filterText, setFilterText] = useState("");
  const [dateFilterInterval, setDateFilterInterval] = useState({});

  // refs
  const dateFilterRef = useRef(null);
  const timeoutRef = useRef(null);

  // redux
  const dispatch = useDispatch();
  const result = useSelector(
    (state) =>
      state.evolutiveCross.configuration.step_one.surveysWithCrossedQuestions
  );

  const { payload: data, running, error } = result;

  //hooks
  const [ handleFlashMessage ] = useFlashMessage();

  useEffect(() => {
    if(error) {
      const { title, message } = EVOLUTIVE_CROSSING_NETWORK_ERROR;

      handleFlashMessage(
        title,
        message,
        'error'
      );
    }
  }, [error])

  function renderLoadingSpinner() {
    return (
      <Ui.ItemsEmpty>
        <Loading size={30} running={running} />
      </Ui.ItemsEmpty>
    );
  }

  /**
   * Effects: Notifica que as perguntas selecionadas devem ser salvas no componente mais externo (EvolutiveView)
   * além disso, como o gancho é o array de itens selecionados, também notificamos quando o botão avançar deve ser
   * habilitado ou desabilitado no passo um.
   */
  useEffect(() => {
    // Managers: Envia as pesquisas selecionadas para o StepManager
    StepManager.updateStepData(0, [...selectedItems]);
    // Muda o estado de visibilidade do botão avançar
    StepManager.changeNextButtonVisibility(!!selectedItems.length);
  }, [selectedItems]);

  useEffect(() => {
    if (customerId) {
      fetchSurveysWithCrossedQuestions(
        customerId,
        null,
        null,
        null,
        dashboardSurveyId,
        dispatch
      );
    }
  }, [customerId, dashboardSurveyId, dispatch]);

  const sendRequestWithParams = useCallback((filterText = null, initialDate = null, endDate = null) => {
    customerId && dashboardSurveyId && fetchSurveysWithCrossedQuestions(
      customerId,
      filterText,
      initialDate,
      endDate,
      dashboardSurveyId,
      dispatch,
    )
  }, [customerId, dashboardSurveyId]);

  const handleOnFilter = useCallback((initialDate, endDate) => {
    if(initialDate === null || endDate === null) {
      /**
       * SOLUÇÃO TEMPORARIA: Conforme solicitado por Renan ao remover a data inicial e/ou final o filtro aplicado é removido
       */
      setDateFilterInterval({initialDate: null, endDate: null});
      sendRequestWithParams(null, null, null);
    } else {
      setDateFilterInterval({initialDate, endDate});
      sendRequestWithParams(null, dateFormat(initialDate), dateFormat(endDate));
    }
    handleOnCancel();
  }, []);

  const handleOnCancel = useCallback(() => {
    dateFilterRef.current && dateFilterRef.current.changeVisibility(false);
  }, [dateFilterRef]);

  /**
   * Callbacks: Parseia os dados das pesquisas com base
   * no retorno do estado do redux.
   */
  const getSurveyParsedData = useCallback((data) => {
    const parsedData = [];

    data && data.forEach(({ id, titulo }) => {
      parsedData.push({
        id,
        mainTitle: titulo,
      });
    });

    const dataWithoutMainSurvey = parsedData.filter(survey => survey.id !== dashboardSurveyId)

    return dataWithoutMainSurvey;
  }, []);

  /**
   * Callbacks: Parseia os dados das perguntas com dado
   * no id da pesquisa atualmente selecionado.
   */
  const getQuestionsParsedData = useCallback(
    (selectedSurveyId) => {
      const parsedData = [];

      const findedItem = data.find(({ id }) => id === selectedSurveyId);

      if (findedItem) {
        parsedData.push(
          ...findedItem.perguntas_principais.map((question) => ({
            id: `${selectedSurveyId}_${question.id}`,
            mainTitle: question.titulo,
          }))
        );
      }

      return parsedData;
    },
    [data]
  );

  /**
   * Callbacks: Controla todo o estado de mudança de pesquisas/perguntas selecionadas
   */
  const handleSelectionChange = useCallback(
    (context, sel) => {
      const { id: surveyId } = context;
      
      setSelectedItems((state) => {
        const temp = [...state];
        const findedInState = temp.findIndex(({ id }) => id === surveyId);

        if (findedInState === -1) {
          const newState = {
            id: surveyId,
            selectedQuestions: [...sel],
          };
          temp.push(newState);
        } else if (!sel.length) {
          temp.splice(findedInState, 1);
        } else {
          temp[findedInState].selectedQuestions = [...sel];
        }

        return temp;
      });
    },
    [selectedItems, setSelectedItems]
  );

  const surveys = getSurveyParsedData(data);

  /**
   * Callbacks: Componente de retorno -> renderiza o widget
   * que lista as informações das alternativas da pergunta expandida
   * e seus respectivos cruzamentos
   */
  const renderWidget = useCallback((itemProps) => {
    // Se o item está expandido
    const { isOpen } = itemProps;
    // SurveyId - QuestionId
    const [sid, qid] = itemProps.id.split('_');

    return (
      <CrossQuestionsAlternatives
        surveyId={sid}
        questionId={qid}
        customerId={customerId}
        withAlternatives
        withCrossedQuestions
        isOpen={isOpen}
      />
    )
  }, [customerId]);

  const QuestionsList = useCallback(
    (itemProps) => {
      const consumedData = getQuestionsParsedData(itemProps.id);

      return (
        <Ui.BodyExpandContainer>
          <ToolTip toolTipTitle="PERGUNTAS DESTA AVALIAÇÃO QUE POSSUEM CRUZAMENTOS CONFIGURADOS" />
          <GenericCollapsibleList
            data={consumedData}
            withExpansibleItems
            withControlledItems
            itemCheckboxStyle={"circle"}
            displayMode={"horizontal"}
            // Este bind irá passar o contexto da pesquisa atualmente selecionada pelo usuário para o método.
            onChangeSelection={handleSelectionChange.bind(null, itemProps)}
            // Garantir a persistência na filtragem
            selectedValues={selectedItems.find(({ id }) => id === itemProps.id)?.selectedQuestions || []}
            childrenDisplayMode="overflow"
            customItemProps={{
              width: "100%",
              renderStyle: "questionList",
            }}
            ChildrenComponent={renderWidget}
          />
        </Ui.BodyExpandContainer>
      );
    },
    [data]
  );

  /**
   * Callbacks: Aplica uma renderização condicional para cada item de acordo com as verificações.
   * contextId será o id do item atualmente iterado.
   */
  const getConditionalItemProps = useCallback((context) => {
    const { id: contextId } = context;

    if (!!selectedItems.find(({ id }) => id === contextId)?.selectedQuestions.length) {
      return {
        Icon: CheckCollapsibleIcon,
      }
    };
  }, [selectedItems]);

  const renderSurveysList = useCallback(() => {
    const itemProps = {
      width: "100%",
      tagTitle: "Título da avaliação",
      withTag: true,
      expansible: true,
      maxHeight: "unset",
      renderStyle: "surveyList",
    };

    return (
      <Ui.CollapsibleContainer>
        <GenericCollapsibleList
          data={surveys}
          withExpansibleItems
          customItemProps={itemProps}
          itemConditionalRenderer={getConditionalItemProps}
          ChildrenComponent={QuestionsList}
        />
      </Ui.CollapsibleContainer>
    );
  }, [surveys, selectedItems]);

  const handleFilterInputChange = useCallback((event) => {
    const currentValue = event.target.value;

    setFilterText(() => {
      return currentValue;
    });

  }, [filterText, sendRequestWithParams]);

  const handleTextFilterOnKeyup = useCallback((event) => {
    const text = event.target.value;

    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = window.setTimeout(() => {
      sendRequestWithParams(text);
    }, 500);
  }, [timeoutRef]);

  return (
    <Ui.Container>
      <Ui.Row>
        <label>
          Selecione perguntas de avaliações anteriores para adicionar ao gráfico
          evolutivo de cruzamentos
        </label>
        <Ui.SearchBox>
          <input
            value={filterText}
            onChange={handleFilterInputChange}
            onKeyUp={handleTextFilterOnKeyup}
            placeholder="Busque por uma avaliação ou pergunta" 
          />
          <Search size={30} color="#00bec1" />
        </Ui.SearchBox>
        <Ui.Filter>
          <Ui.HandleFilter
            onClick={() => dateFilterRef.current.changeVisibility(true)}
          >
            <Ui.FilterTextContent hasDate={dateFilterInterval.initialDate && dateFilterInterval.endDate}>
              <CalendarModalIcon />
              <Ui.FilterText hasDate={dateFilterInterval.initialDate && dateFilterInterval.endDate}>
                {dateFilterInterval.initialDate && dateFilterInterval.endDate ? (
                  <>
                    {dateFilterInterval.initialDate.toLocaleDateString()}
                    <ArrowRightIcon />
                    {dateFilterInterval.endDate.toLocaleDateString()}
                  </>
                ): "Filtrar por período"}
              </Ui.FilterText>
            </Ui.FilterTextContent>
            <span className="iconContainer">
              <ArrowDownIcon />
            </span>
          </Ui.HandleFilter>
          <DateFilter
            ref={dateFilterRef}
            handleOnFilter={handleOnFilter}
            handleOnCancel={handleOnCancel}
            filterInterval={dateFilterInterval}
          />
        </Ui.Filter>
      </Ui.Row>
      {running 
        ? renderLoadingSpinner()
        : renderSurveysList()
      }
    </Ui.Container>
  );
}